import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import type { Locale } from "date-fns";
import {
  dayKey,
  leaveDaysInMonth,
  leaveIntersectsMonth,
  yearDayInterval,
} from "features/leave-calendar/leaveCalendarDates";
import type {
  LeaveRecord,
  LeaveRequestStatus,
} from "features/leave-calendar/leaveCalendar.types";

/** The wall calendar's mini month grids start the week on Monday, like the company calendar. */
export const WALL_CALENDAR_WEEK_STARTS_ON = 1 as const;

/**
 * Full Mon–Sun weeks covering a month, including the leading/trailing days of
 * the neighbouring months needed to complete the grid. Callers only render
 * the days that belong to `monthIndex` — the rest are blank filler cells, so
 * a leave on a filler day is shown on its own month's tile instead.
 */
export function getMonthGridWeeks(year: number, monthIndex: number): Date[][] {
  const monthStart = startOfMonth(new Date(year, monthIndex, 1));
  const monthEnd = endOfMonth(monthStart);
  const gridStart = startOfWeek(monthStart, {
    weekStartsOn: WALL_CALENDAR_WEEK_STARTS_ON,
  });
  const gridEnd = endOfWeek(monthEnd, {
    weekStartsOn: WALL_CALENDAR_WEEK_STARTS_ON,
  });
  const allDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weeks: Date[][] = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }
  return weeks;
}

/** Two-letter Mon…Sun weekday initials for the given locale (e.g. "Mo"/"Pr"). */
export function weekdayInitials(locale: Locale): string[] {
  const start = startOfWeek(new Date(2024, 0, 1), {
    weekStartsOn: WALL_CALENDAR_WEEK_STARTS_ON,
  });
  return Array.from({ length: 7 }, (_, i) =>
    format(addDays(start, i), "EEEEEE", { locale })
  );
}

/** Statuses the wall calendar paints. Cancelled/rejected/draft requests stay hidden (see task spec). */
const DISPLAYABLE_STATUSES: ReadonlySet<LeaveRequestStatus> = new Set<LeaveRequestStatus>([
  "approved",
  "pending_approval",
]);

export function isDisplayableLeaveStatus(status?: string | null): boolean {
  return !!status && DISPLAYABLE_STATUSES.has(status as LeaveRequestStatus);
}

/** Keeps only the leave requests the wall calendar is allowed to paint. */
export function filterDisplayableLeaves(leaves: LeaveRecord[]): LeaveRecord[] {
  return leaves.filter((l) => isDisplayableLeaveStatus(l.status));
}

/**
 * Day → leave lookup for a whole year. A leave spanning into the previous or
 * next year only contributes the days that actually fall in `year` (reuses
 * the same month-clipping helpers the company calendar uses, with year-wide
 * bounds instead of month-wide ones). When two leaves cover the same day
 * (should not normally happen) the first one found wins, matching
 * LeaveCalendarUserRow's per-day map.
 */
export function buildYearLeaveDayMap(
  leaves: LeaveRecord[],
  year: number
): Map<string, LeaveRecord> {
  const { start, end } = yearDayInterval(year);
  const map = new Map<string, LeaveRecord>();
  const displayable = filterDisplayableLeaves(leaves).filter((l) =>
    leaveIntersectsMonth(l.startDate, l.endDate, start, end)
  );
  for (const leave of displayable) {
    for (const d of leaveDaysInMonth(
      leave.startDate,
      leave.endDate,
      start,
      end
    )) {
      const key = dayKey(d);
      if (!map.has(key)) {
        map.set(key, leave);
      }
    }
  }
  return map;
}
